import { useState, useEffect, useCallback } from 'react';
import { generateStableListKey } from '../utils/helpers';
import { get, postJson, putJson, del as apiDelete } from '../utils/apiClient';

/**
 * Hook for sharing and access management
 */
export default function useSharing({ token, user, setCache, setSelectedListId }) {
  const [shareInvitation, setShareInvitation] = useState(null);
  const [listShares, setListShares] = useState({});
  const [showShareModal, setShowShareModal] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // Handle share link when app loads
  useEffect(() => {
    const handleShareLink = async () => {
      const path = window.location.pathname;
      const joinMatch = path.match(/^\/join\/(.+)$/);

      let listIdToJoin = null;

      if (joinMatch && token) {
        listIdToJoin = joinMatch[1];
      } else if (shareInvitation && token && !joinMatch) {
        listIdToJoin = shareInvitation.listId;
      }

      if (listIdToJoin && token) {
        try {
          await postJson(`/lists/${listIdToJoin}/join`, {});

          // Refresh lists
          try {
            const { data: listsData } = await get(`/lists`);
            const listsWithKeys = listsData.map(list => ({
              ...list,
              stableKey: generateStableListKey(list)
            }));
            setCache(c => ({ ...c, lists: listsWithKeys }));
          } catch (refreshError) {
            console.error(`❌ [Share Link] Failed to refresh lists:`, refreshError);
          }

          setSelectedListId(listIdToJoin);
          setShareInvitation(null);

          if (joinMatch) {
            window.history.replaceState({}, '', '/');
          }

        } catch (error) {
          console.error(`❌ [Share Link] Failed to join list: ${listIdToJoin}`, error);
          let message = "Failed to join list";
          if (error.response?.status === 404) {
            message = "List not found or not accessible";
          } else if (error.response?.status === 403) {
            message = "You don't have permission to access this list";
          } else if (error.response?.data?.message) {
            message = error.response.data.message;
          }
          alert(message);

          if (joinMatch) {
            window.history.replaceState({}, '', '/');
          }
          setShareInvitation(null);
        }
      } else if (joinMatch && !token) {
        const listId = joinMatch[1];

        try {
          const { data: publicLists } = await get(`/lists/public`);
          const targetList = publicLists.find(list => list.id === listId);

          if (targetList) {
            setShareInvitation({
              listId: listId,
              listName: targetList.name,
              sharedBy: 'Someone',
              isPublic: true
            });
          } else {
            setShareInvitation({
              listId: listId,
              listName: 'a shared list',
              sharedBy: 'Someone',
              isPublic: false
            });
          }
        } catch (error) {
          setShareInvitation({
            listId: listId,
            listName: 'a shared list',
            sharedBy: 'Someone',
            isPublic: false
          });
        }

        window.history.replaceState({}, '', '/');
      }
    };

    handleShareLink();
  }, [token, shareInvitation, setCache, setSelectedListId]);

  // Fetch shared users for a list
  const fetchListShares = useCallback(async (listId) => {
    try {
      const { data } = await get(`/lists/${listId}/shares`);

      setListShares(prev => ({ ...prev, [listId]: data }));
      return data;
    } catch (error) {
      console.error(`❌ [Shares] Failed to fetch shares for list ${listId}:`, error);
      return [];
    }
  }, []);

  // Update user access permission
  const handleUpdateAccess = useCallback(async (listId, userId, permission) => {
    try {
      await putJson(`/lists/${listId}/shares/${userId}`, { permission });

      await fetchListShares(listId);
    } catch (error) {
      console.error('❌ [Access] Failed to update access:', error);
      alert('Failed to update access: ' + (error.response?.data?.message || error.message));
    }
  }, [fetchListShares]);

  // Remove user access
  const handleRemoveAccess = useCallback(async (listId, userId) => {
    if (!window.confirm('Are you sure you want to remove access for this user?')) return;

    try {
      await apiDelete(`/lists/${listId}/shares/${userId}`);
      await fetchListShares(listId);
    } catch (error) {
      if (error?.response?.status === 404) return;
      console.error('❌ [Access] Failed to remove access:', error);
      alert('Failed to remove access: ' + (error.response?.data?.message || error.message));
    }
  }, [fetchListShares]);

  // Handle managing access for shared users
  const handleManageAccess = useCallback((list, propertyKey, email) => {
    if (!propertyKey.startsWith('shared_')) return;

    const shares = listShares[list.id] || [];
    const share = shares.find(s => s.email === email);

    if (!share) return;

    const options = [
      { label: '👁️ View Only', value: 'view' },
      { label: '✏️ Edit', value: 'edit' },
      { label: '👑 Admin', value: 'admin' },
      { label: '🗑️ Remove Access', value: 'remove' }
    ];

    const action = prompt(
      `Manage access for ${email}\nCurrent: ${share.permission}\n\nEnter number:\n` +
      options.map((opt, i) => `${i + 1}. ${opt.label}`).join('\n')
    );

    const selectedIndex = parseInt(action) - 1;
    if (selectedIndex >= 0 && selectedIndex < options.length) {
      const selectedOption = options[selectedIndex];

      if (selectedOption.value === 'remove') {
        handleRemoveAccess(list.id, share.user_id);
      } else {
        handleUpdateAccess(list.id, share.user_id, selectedOption.value);
      }
    }
  }, [listShares, handleUpdateAccess, handleRemoveAccess]);

  // Get share link for a list
  const getShareLinkForList = useCallback((listId) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/join/${listId}`;
  }, []);

  // Copy share link to clipboard
  const handleCopyLinkForList = useCallback(async (listId) => {
    try {
      const shareLink = getShareLinkForList(listId);
      await navigator.clipboard.writeText(shareLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
      // Fallback for older browsers
      const shareLink = getShareLinkForList(listId);
      const textArea = document.createElement('textarea');
      textArea.value = shareLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  }, [getShareLinkForList]);

  // Check if user can share a list
  const canUserShare = useCallback((selectedList) => {
    if (!selectedList) return false;
    return selectedList.isOwner || (selectedList.shareInfo && selectedList.shareInfo.permission === 'admin');
  }, []);

  return {
    shareInvitation,
    setShareInvitation,
    listShares,
    showShareModal,
    setShowShareModal,
    linkCopied,
    fetchListShares,
    handleManageAccess,
    handleUpdateAccess,
    handleRemoveAccess,
    getShareLinkForList,
    handleCopyLinkForList,
    canUserShare
  };
}
